import { useCallback, useMemo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';
import { ArrowDropDown } from '@mui/icons-material';
import EllipsisTypography from '../EllipsisTypography/EllipsisTypography';
import AssetAndGroupsButtons from '../NRAISearch/NRAISearchFilters/NRAutocomplete/AssetAndGroupsButtons';
import NRVirtualizedListBox from '../NRVirtualizedLists/NRVirtualizedListBox';
import { useNRAutocompleteSelect } from './useNRAutocompleteSelect';

/**
 * Multi-select + filter using MUI Select + Menu (not Autocomplete), for easier screen-reader testing.
 * Mirrors key NRAutocomplete props used by group/asset pickers (non-label trigger path).
 */
function NRAutocomplete({
  arrayData = [],
  value = [],
  setValue,
  loading,
  fullWidth = false,
  title,
  dataNameSingular = 'Group',
  fieldToShow = 'name',
  idField = 'id',
  multiple = true,
  disabled = false,
  showAlwaysTitle = false,
  assetsAndGroups = false,
  menuDisablePortal = true,
  menuPaperZIndex,
  onOpenChange = () => {},
  helperText = '',
  virtualizationThreshold = 120
}) {
  const theme = useTheme();
  const resolvedMenuPaperZIndex = menuPaperZIndex ?? (menuDisablePortal ? theme.zIndex.modal : theme.zIndex.tooltip);
  const checkIconSx = useMemo(
    () => ({
      width: 17,
      height: 17,
      marginRight: '5px',
      marginLeft: '-2px'
    }),
    []
  );

  const {
    open,
    filter,
    setFilter,
    menuPlacement,
    selectContainerRef,
    menuPaperRef,
    filterInputRef,
    assetGroupFirstButtonRef,
    assetGroupLastButtonRef,
    selectedList,
    selectedIds,
    selectedIdSet,
    filteredOptions,
    displayValue,
    selectDisabled,
    showClear,
    useVirtualizedList,
    MENU_PAPER_MAX_HEIGHT_PX,
    handleOpen,
    handleClose,
    handleOptionToggle,
    handleSelectChangeNoop,
    clearAllSelections,
    focusFilterInput,
    handleMenuListKeyDown,
    handlePaperKeyDownCapture,
    registerOptionRowRef,
    handleFilterKeyDown
  } = useNRAutocompleteSelect({
    arrayData,
    value,
    setValue,
    loading,
    dataNameSingular,
    fieldToShow,
    idField,
    multiple,
    disabled,
    assetsAndGroups,
    onOpenChange,
    virtualizationThreshold
  });

  const renderMenuItem = useCallback(
    option => {
      const oid = option[idField];
      const checked = selectedIdSet.has(String(oid));
      const isEmptyOption = oid === '' || oid === undefined;
      const optionLabel = String(option[fieldToShow] || '');
      return (
        <MenuItem
          key={String(oid) || 'empty'}
          ref={el => registerOptionRowRef(oid, el)}
          value={oid}
          data-option-id={String(oid)}
          aria-selected={checked}
          aria-label={`${checked ? 'Unselect' : 'Select'} ${optionLabel}`}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleOptionToggle(oid);
          }}
          tabIndex={0}
          sx={{
            py: 1,
            pl: 0,
            alignItems: 'flex-start',
            bgcolor: 'background.paper',
            ...(multiple && selectedList.length === 0 && isEmptyOption
              ? { bgcolor: theme.palette.selectedBackground?.selector ?? theme.palette.action.selected }
              : {})
          }}
        >
          <Checkbox
            size="small"
            aria-hidden="true"
            checked={checked}
            disableRipple
            tabIndex={-1}
            inputProps={{ tabIndex: -1 }}
            icon={<DoneIcon sx={{ ...checkIconSx, visibility: 'hidden' }} />}
            checkedIcon={<DoneIcon sx={checkIconSx} />}
            sx={{
              padding: theme.spacing(0.6),
              color: 'text.secondary',
              '&.Mui-checked': { color: 'text.secondary' },
              '& .MuiSvgIcon-root': { fontSize: 17, margin: 0 }
            }}
          />
          <ListItemText
            primary={
              <Typography
                variant="body1"
                color="text.primary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {option[fieldToShow]}
              </Typography>
            }
            sx={{ my: 0 }}
          />
        </MenuItem>
      );
    },
    [checkIconSx, fieldToShow, handleOptionToggle, idField, multiple, registerOptionRowRef, selectedIdSet, selectedList.length, theme]
  );

  const renderValue = useCallback(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden', gap: 0.5 }}>
        {!!showAlwaysTitle && !!title && <>{title}</>}
        {!showAlwaysTitle && !arrayData?.length && !!loading && <Typography variant="body1">{`Loading ${dataNameSingular}s...`}</Typography>}
        {!showAlwaysTitle && !arrayData?.length && !loading && <Typography variant="body1">{`No ${dataNameSingular}s Present`}</Typography>}
        {!showAlwaysTitle && !!arrayData?.length && (
          <EllipsisTypography disableTooltipTabStop variant="body1" color="text.primary" sx={{ flexGrow: 1, textAlign: 'left' }}>
            {displayValue || title || `${dataNameSingular}s`}
          </EllipsisTypography>
        )}
      </Box>
    ),
    [arrayData?.length, dataNameSingular, displayValue, loading, showAlwaysTitle, title]
  );

  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 'fit-content',
        minWidth: fullWidth ? 'unset' : theme.spacing(29),
        fontSize: 16,
        ...(disabled ? { pointerEvents: 'none', opacity: 0.5 } : {})
      }}
    >
      <FormControl ref={selectContainerRef} fullWidth={fullWidth} sx={{ minWidth: fullWidth ? 'unset' : theme.spacing(29), position: 'relative' }}>
        <Select
          multiple={multiple}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          displayEmpty
          value={multiple ? selectedIds : selectedIds[0] ?? ''}
          onChange={handleSelectChangeNoop}
          disabled={selectDisabled}
          renderValue={renderValue}
          IconComponent={props => <ArrowDropDown {...props} sx={{ color: theme.palette.text.secondary }} />}
          MenuProps={{
            disablePortal: menuDisablePortal,
            autoFocus: false,
            anchorOrigin: {
              vertical: menuPlacement === 'top' ? 'top' : 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: menuPlacement === 'top' ? 'bottom' : 'top',
              horizontal: 'left'
            },
            PaperProps: {
              ref: menuPaperRef,
              onKeyDownCapture: handlePaperKeyDownCapture,
              sx: {
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 24px ${
                  theme.palette.mode === 'light' ? alpha(theme.palette.neutral[900], 0.2) : alpha(theme.palette.common.black, 0.9)
                }`,
                borderRadius: 1.5,
                maxHeight: MENU_PAPER_MAX_HEIGHT_PX,
                width: theme.spacing(37.5),
                zIndex: resolvedMenuPaperZIndex,
                ...(menuPlacement === 'top' ? { mb: theme.spacing(0.5) } : { mt: 0 }),
                bgcolor: 'background.paper',
                overflow: 'hidden',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }
            },
            MenuListProps: {
              variant: 'menu',
              autoFocus: false,
              dense: true,
              disablePadding: true,
              sx: {
                flex: '1 1 auto',
                minHeight: 0,
                maxHeight: MENU_PAPER_MAX_HEIGHT_PX,
                overflowX: 'hidden',
                overflowY: useVirtualizedList ? 'hidden' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                py: 0,
                outline: 0
              },
              onKeyDown: handleMenuListKeyDown
            }
          }}
          sx={{
            bgcolor: theme.palette.background.paper,
            width: fullWidth ? '100%' : 'fit-content',
            minWidth: fullWidth ? 'unset' : theme.spacing(29),
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.input.border
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.input.border
            },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              py: 1,
              ...(showClear ? { pr: `${theme.spacing(10)} !important` } : {})
            }
          }}
          inputProps={{ 'aria-label': typeof title === 'string' ? title : `${dataNameSingular} selector` }}
        >
          <ListSubheader
            sx={{
              p: 0,
              bgcolor: 'background.paper',
              lineHeight: 1,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              flexShrink: 0
            }}
            onClick={e => e.stopPropagation()}
          >
            {!!assetsAndGroups && (
              <AssetAndGroupsButtons
                onRequestFocusFilter={focusFilterInput}
                firstToggleButtonRef={assetGroupFirstButtonRef}
                lastToggleButtonRef={assetGroupLastButtonRef}
              />
            )}
            <Box
              sx={{
                px: 2,
                pt: 1.25,
                pb: 1,
                borderBottom: `1px solid ${theme.palette.divider}`
              }}
            >
              <TextField
                size="small"
                fullWidth
                inputRef={filterInputRef}
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={`Filter ${dataNameSingular}s`}
                aria-label={`Filter ${dataNameSingular}s`}
                autoFocus
                variant="outlined"
                onKeyDown={handleFilterKeyDown}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderWidth: '1px',
                    fontSize: 14,
                    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                      borderWidth: '1px'
                    },
                    '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                      borderWidth: '1px'
                    },
                    '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                      borderWidth: '1px'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 1,
                    px: 1
                  }
                }}
                InputProps={{
                  sx: { borderRadius: 1 }
                }}
              />
            </Box>
          </ListSubheader>
          {useVirtualizedList ? (
            <NRVirtualizedListBox>{filteredOptions.map(renderMenuItem)}</NRVirtualizedListBox>
          ) : (
            filteredOptions.map(renderMenuItem)
          )}
        </Select>
        {showClear && !selectDisabled && (
          <IconButton
            type="button"
            size="small"
            aria-label={`Clear all selected ${dataNameSingular.toLowerCase()}s`}
            onClick={clearAllSelections}
            sx={{
              position: 'absolute',
              right: theme.spacing(4),
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </FormControl>
      {!!helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

export default NRAutocomplete;
